import { animate, state, style, transition, trigger } from '@angular/animations';
import { Overlay } from '@angular/cdk/overlay';
import { NgClass } from '@angular/common';
import {
    AfterViewInit,
    Component,
    ElementRef,
    EventEmitter,
    HostBinding,
    HostListener,
    Inject,
    Input,
    OnInit,
    Output,
    QueryList,
    ViewChild,
    ViewChildren,
    ViewContainerRef,
} from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormGroup } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatSort, MatSortModule, Sort, SortDirection } from '@angular/material/sort';
import { MatTableModule } from '@angular/material/table';
import { Moment } from 'moment';
import { InfiniteScrollDirective, InfiniteScrollModule } from 'ngx-infinite-scroll';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { ISearchable } from 'src/app/controls/common/isearchable';
import { FilterHandler } from 'src/app/core/filter-handler';
import { EnumHelper } from 'src/app/helpers/enum-helper';
import { IFilterable } from 'src/app/models/interfaces/filterable.interface';
import { ISortable } from 'src/app/models/interfaces/sortable.interface';
import { MenuItem } from '../common/menu-item';
import { ContextualMenuComponent } from '../contextual-menu/contextual-menu.component';
import { ContextualMenuChange } from '../contextual-menu/models/contextual-menu-change';
import { FormFocusDirective } from '../directives/form-focus.directive';
import { FiltersBarComponent } from '../filters-bar/filters-bar.component';
import { BaseFilterComponent } from '../filters/base-filter/base-filter.component';
import { DateRange } from '../filters/models/date-range';
import { FilterEvent } from '../filters/models/filter-event';
import { FilterType } from '../filters/models/filter-type';
import { ListItem } from '../filters/models/list-item';
import { NumericComparison } from '../filters/models/numeric-comparison';
import { SortDialogResult } from '../sort-dialog/models/sort-dialog-result';
import { SortDialogComponent } from '../sort-dialog/sort-dialog.component';
import { AdvancedGridviewModel } from './models/advanced-gridview-model';
import { ColumnListDefinition } from './models/column-list-definition';
import { ColumnType } from './models/column-type';
import { DatasourceColumnDefinitionInterface } from './models/datasource-column-definition-interface';
import { FilterInfos } from './models/filter-infos';
import { MenuITemAction } from './models/menu-item-action';
import { MenuItemEvent } from './models/menu-item-event';
import { RowClickEvent } from './models/row-click-event';
import { FilterServiceInterface } from './services/filter-service-interface';
import { TableCellComponent } from './table-cell/table-cell.component';
import { TableHeaderCellComponent } from './table-header-cell/table-header-cell.component';

@Component({
    selector: 'app-advanced-gridview',
    templateUrl: './advanced-gridview.component.html',
    styleUrls: ['./advanced-gridview.component.scss'],
    animations: [
        trigger('detailExpand', [
            state('void', style({
                height: '0px',
                overflow: 'hidden',
            })),
            state('void', style({
                height: '0px',
                minHeight: '0',
                visibility: 'hidden',
                overflow: 'hidden',
            })),
            state('collapsed', style({ height: '0px', minHeight: '0', visibility: 'hidden' })),
            state('expanded', style({ height: '*', visibility: 'visible' })),
            transition('* => expanded', animate('500ms ease-in-out')),
            transition('expanded => *', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
        ]),
    ],
    imports: [
        FiltersBarComponent,
        FormsModule,
        FormFocusDirective,
        ReactiveFormsModule,
        MatTableModule,
        MatSortModule,
        InfiniteScrollModule,
        NgClass,
        TableHeaderCellComponent,
        TableCellComponent,
        MatIconModule,
        ContextualMenuComponent,
        MatButtonModule,
    ]
})
export class AdvancedGridviewComponent<T> implements OnInit, AfterViewInit, IFilterable {
    /** Pour être utilisable dans le template */
    FilterType: any = FilterType;
    ColumnType: any = ColumnType;

    public listItems: { [id: string]: ListItem<string>[] } = {};

    listItemsDatabase: { [id: string]: ListItem<string>[] } = {};

    columnStyles: { [id: string]: string } = {};
    private filterEventHandlers: Observable<FilterEvent>[] = [];

    /** Formulaire contenant les filtres du tableau. */
    filtersForm: UntypedFormGroup;

    /** Model de la table filtrée */
    @Input()
    model: AdvancedGridviewModel;

    @Input()
    dataSource: ISearchable;

    @Input()
    expandableRow: boolean = false;

    @Input()
    sortEnabled: boolean = false;

    from: number = 0;

    @Input()
    size: number;

    @Input()
    public readOnly: boolean = false;

    @Input()
    public className: string;

    @Input()
    public creationIcon: boolean = false;

    @ViewChild(MatSort, { static: true })
    sort: MatSort;

    @ViewChild('input') input: ElementRef;

    _menuItems: MenuItem<T>[];
    @Input()
    set menuItems(value: MenuItem<T>[]) {
        this._menuItems = value;
    }

    get menuItems(): MenuItem<T>[] {
        return this._menuItems;
    }

    @Output()
    public menuClicked: EventEmitter<MenuItemEvent<T>> = new EventEmitter();

    @Output()
    public rowDoubleClicked: EventEmitter<RowClickEvent<T>> = new EventEmitter();

    @Output()
    public rowClicked: EventEmitter<RowClickEvent<T>> = new EventEmitter();

    @Output()
    public itemsSorted: EventEmitter<ISortable[]> = new EventEmitter();

    @Output()
    public createIconClicked: EventEmitter<void> = new EventEmitter();

    /**
     * Paramètres du composant de scrolling virtuel : Nombre de millisecondes, après que l'utilisateur stoppe le scroll,
     * au dela desquelles l'événement est émis.
     */
    throttle = 50;

    /**
     * Paramètres du composant de scrolling virtuel : Valeur permettant de determiner a quel distance du bas du containeur,
     * un nouvel événement scroll est emis. Cette valeur est multiplié par 10 pour déterminer le pourcentage de distance.
     */
    scrollDistance = 2;

    /**
     * Paramètres du composant de scrolling virtuel : Valeur permettant de determiner a quel distance du haut du containeur,
     * un nouvel événement scroll est emis. Cette valeur est multiplié par 10 pour déterminer le pourcentage de distance.
     */
    scrollUpDistance = 2;

    /** Référence à la directive du scroll virtuel */
    @ViewChild(InfiniteScrollDirective, { static: true })
    infiniteScrollReference: InfiniteScrollDirective;

    /** Reference à l'élément HTML Table. */
    @ViewChild('AdvancedTable', { read: ElementRef, static: true })
    private advancedTableElementRef: ElementRef;

    @ViewChildren(BaseFilterComponent)
    filterChildren: QueryList<BaseFilterComponent>;

    filters: FilterInfos[] = [];

    searchTerm: string = null;

    searchTermFilters: FilterInfos[] = [];

    public filterHandler: FilterHandler;

    public getSortableItemsFunction: (item: ISortable) => Observable<ISortable[]>;

    private scrollingInProgress: boolean = false;

    protected lastScrollTop: number = 0;

    protected currentScrollTop: number = 0;

    loadedRecordsCount: number = 0;

    /* Section Expandable row */

    isExpansionDetailRow = (i: number, row: Object) => {
        return row === this.expandedElement;
    };
    expandedElement: any;

    public onSelected(row, event) {
        console.log('simple clic');
        if (event.srcElement.id !== 'actionIcon' && event.srcElement.id !== 'menuIcon' && event.srcElement.id !== 'sortIcon') {
            if (this.expandableRow) {
                this.expandedElement === row ? (this.expandedElement = null) : (this.expandedElement = row);
                this.dataSource.refresh();
            } else {
                let evt = new RowClickEvent<T>();
                evt.context = row;
                this.rowClicked.emit(evt);
            }
        }
    }

    /**
     * Appelé lorsque l'utilisateur double-clique sur un élément de la liste.
     */
    onMouseDoubleClick(row, event) {
        console.log('double clic');
        let evt = new RowClickEvent<T>();
        evt.context = row;
        this.rowDoubleClicked.emit(evt);
    }

    /**
     * Appelé lorsque l'utilisateur clique sur le bouton de création.
     */
    onCreate() {
        console.log('create new element');
        this.createIconClicked.emit();
    }

    @HostBinding('@detailExpand') popupAnimBinding;

    @HostListener('@detailExpand.start', ['$event'])
    public animationStarted($event) {}

    @HostListener('@detailExpand.done', ['$event'])
    public animationFinished($event) {}

    /* Fin Section Expandable row */

    constructor(
        public dialog: MatDialog,
        public enumHelper: EnumHelper,
        @Inject('FilterServiceInterface')
        private filtersService: FilterServiceInterface,
        public overlay: Overlay,
        public viewContainerRef: ViewContainerRef
    ) {
        this.filtersForm = new UntypedFormGroup({});
        this.filterHandler = new FilterHandler(this);
    }

    ngOnInit() {
        console.log('AdvancedGridviewComponent --> ngOnInit');
        let defaultSort: Sort = { active: this.model.defaultSort.columnName, direction: this.model.defaultSort.direction as SortDirection };

        this.loadMessages(this.filters, this.searchTermFilters, defaultSort.active, defaultSort.direction, this.from, this.size, false);

        this.sort.active = defaultSort.active;
        this.sort.direction = defaultSort.direction;
        this.sort.sortChange.emit(defaultSort);

        this.model.displayedColumnsDefinitions
            .filter((item) => this.enumHelper.areEquals(ColumnType, item.type, ColumnType.List))
            .forEach((item, index) => {
                // Cas des filtres de type liste
                this.loadFilterItems(item as ColumnListDefinition<string>);
            });

        let tableWidth = (this.advancedTableElementRef.nativeElement as HTMLDivElement).getBoundingClientRect().width;
    }

    ngAfterViewInit() {
        this.initializeFilters();

        this.sort.sortChange
            .pipe(
                tap(() => {
                    // Réinitialise la borne de départ
                    this.from = 0;
                    this.loadMessages(this.filters, this.searchTermFilters, this.sort.active, this.sort.direction, this.from, this.size, false);
                })
            )
            .subscribe();
    }

    /**
     * Initialise les filtres du tableau de résultat.
     */
    initializeFilters() {
        this.filterChildren.toArray().forEach((item, index) => {
            var handler = item.register();

            this.filterEventHandlers.push(handler);

            handler.subscribe((x) => {
                const previousFilters = [...this.filters];

                const index = this.filters.findIndex((f) => f.ColumnName == x.columnName);
                if (index > -1) {
                    this.filters.splice(index, 1);
                }

                let filter = new FilterInfos();
                filter.ColumnName = x.columnName;

                filter.FilterType = x.type;

                if (typeof x.value === 'string') {
                    filter.Value = x.value;
                }
                if (typeof x.value === 'number') {
                    filter.Value = x.value.toString();
                }
                if (typeof x.value === 'boolean') {
                    filter.Value = x.value.toString();
                } else if (Array.isArray(x.value)) {
                    filter.Value = x.value.join('|');
                    filter.SelectionMultiple = true;
                }
                // else if (x.value instanceof Date) {
                //   filter.Value = (x.value as Date).toLocaleDateString();
                // }
                else if (filter.FilterType === FilterType.Date) {
                    const selectedDate = x.value as Moment;

                    if (selectedDate) {
                        filter.Value = selectedDate.format('YYYY-MM-DD');
                    } else {
                        filter.Value = null;
                    }
                } else if (x.value instanceof DateRange) {
                    filter.Value = JSON.stringify(x.value);
                } else if (x.value instanceof NumericComparison) {
                    let numericComparisonValue = x.value as NumericComparison;

                    if (numericComparisonValue.comparisonType != null && numericComparisonValue.value != null) {
                        filter.Value = JSON.stringify(x.value);
                    } else {
                        filter.Value = null;
                    }
                }

                if (filter.Value != null && filter.Value != undefined && filter.Value.length !== 0) {
                    this.filters.push(filter);
                }

                if (previousFilters.length == 0 && this.filters.length == 0) {
                    return;
                }

                this.from = 0;
                this.loadMessages(this.filters, this.searchTermFilters, this.sort.active, this.sort.direction, this.from, this.size, false);
            });
        });
    }

    onScrollDown(event: any) {
        const elem = event.target;
        this.currentScrollTop = elem.scrollTop;
        // this.currentScrollLeft = elem.scrollLeft;

        if (
            this.scrollingInProgress === false &&
            elem.offsetHeight + elem.scrollTop >= elem.scrollHeight &&
            elem.scrollTop > this.lastScrollTop &&
            elem.offsetHeight + this.lastScrollTop < elem.scrollHeight
        ) {
            // Chargement de la "page suivante" si un chargement n'est pas déjà en cours & si le scrolling dépasse la hauteur de la barre de défilement
            // & si le scroll est vers le bas & si le precedent scroll n'avait pas déjà atteint la fin de la liste
            this.scrollingInProgress = true;
            // this.spinner.show('folders-spinner');
            this.lastScrollTop = elem.scrollTop;

            this.loadMessages(this.filters, this.searchTermFilters, this.sort.active, this.sort.direction, this.from, this.size, true);
        }
    }

    private loadFilterItems(column: DatasourceColumnDefinitionInterface<string>): void {
        if (column.autoFill) {
            this.filtersService.Get(column.columnName).subscribe((filter) => {
                var x = filter;

                if (x.length == 0) {
                    x = this.listItemsDatabase[column.columnName];
                }

                this.loadFilter(column.columnName, x);

                if (!!column.dataSource) {
                    x.forEach((item, index) => {
                        column.dataSource.set(item.value, item);
                    });
                }
            });
        }
    }

    public SetFilterItems(column: DatasourceColumnDefinitionInterface<string>, items: ListItem<string>[]): void {
        console.log('Set filter items', column, items);
        this.listItemsDatabase[column.columnName] = items;

        this.loadFilter(column.columnName, items);
        var columnDefinition = this.filterHandler.filterredColumnDefinitions.find((x) => x.columnName === column.columnName) as ColumnListDefinition<string>;
        if (!!columnDefinition?.dataSource) {
            items.forEach((item, index) => {
                columnDefinition.dataSource.set(item.value, item);
            });
        } else {
            console.log('warning');
        }

        if (!!columnDefinition) {
            columnDefinition.values = items;
        } else {
            console.log('warning');
        }

        console.log('column.dataSource', columnDefinition.dataSource);
    }

    public loadFilter(columnName: string, listItem: ListItem<string>[]): void {
        this.listItems[columnName] = listItem;
    }

    private loadMessages(filters: FilterInfos[], generalfilters: FilterInfos[], sortColumn = '', sortDirection = 'asc', pageIndex = 0, pageSize = 100, scroll: boolean) {
        if (scroll) {
            this.from = pageIndex + pageSize;
            this.scrollingInProgress = false;
            pageIndex = this.from;
        }

        if (!scroll) {
            this.resetScroll();
        }

        this.dataSource.loadRecords(filters, generalfilters, sortColumn, sortDirection, pageIndex, pageSize, scroll);
        this.loadedRecordsCount = this.dataSource.getRecordsCount();
    }

    /**
     * Réinitialise le scroll de la table.
     */
    public resetScroll() {
        this.from = 0;
        this.scrollingInProgress = false;

        if (this.advancedTableElementRef != null) {
            this.advancedTableElementRef.nativeElement.scrollTop = 0;
        }
    }

    onResetFilters() {
        this.filters = [];
        this.searchTerm = '';
        this.searchTermFilters = [];

        this.from = 0;

        this.filterChildren.toArray().forEach((item, index) => {
            item.reset();
        });
        this.loadMessages(this.filters, this.searchTermFilters, this.sort.active, this.sort.direction, this.from, this.size, false);
    }

    public refresh() {
        this.resetScroll();
        this.loadMessages(this.filters, this.searchTermFilters, this.sort.active, this.sort.direction, this.from, this.size, false);
    }

    public onMenuChanged(event: ContextualMenuChange<T, MenuITemAction>) {
        let evt = new MenuItemEvent<T>();
        evt.action = event.action;
        evt.context = <T>event.context;
        this.menuClicked.next(evt);
    }

    @Input()
    contextualMenu: boolean = true;

    @ViewChild(ContextualMenuComponent, { static: false })
    contextuelMenu: ContextualMenuComponent<T, MenuITemAction>;

    public menu(event: MouseEvent, row: T) {
        if (this.contextualMenu === true) {
            this.contextuelMenu.open(event, row);
        }
    }

    public displaySortDialog(event: MouseEvent, row: ISortable) {
        console.log('Display sort dialog');

        let items = [];

        if (this.getSortableItemsFunction) {
            this.getSortableItemsFunction(row).subscribe((items) => {
                const dialogRef = this.dialog.open(SortDialogComponent, {
                    maxWidth: '400px',
                    data: {
                        title: "Détermination de l'ordre des éléments",
                        items: JSON.parse(JSON.stringify(items)), // Deep Copy pour garder une trace de la l'ordre d'orgine pour la comparaison ci-dessous.
                    },
                    panelClass: 'empty-dialog',
                });

                // Attente de la réponse de la boite de dialogue de saisie.
                dialogRef.afterClosed().subscribe((dialogResult: SortDialogResult) => {
                    if (!dialogResult.canceled) {
                        // Mise à jour de l'ordre des éléements.
                        console.log("Mise à jour de l'ordre des elements effectuée");

                        if (this.itemsSorted) {
                            let updatedItems = dialogResult.items.filter((item) => items.findIndex((x) => x.id == item.id && x.order !== item.order) !== -1);

                            this.itemsSorted.emit(updatedItems);
                        }
                    }
                });
            });
        } else {
            throw new Error("Le délégué getSortableItemsFunction n'est pas renseigné");
        }
    }

    /**
     * Applique les filtres de recherche et/ou de colonnes.
     */
    public applyFilters() {
        console.log('Apply filters');
        // TODO
        //this.spinner.show('folders-spinner');
        this.applyColumnsFilters();
        this.applyGeneralFilters();

        this.loadMessages(this.filters, this.searchTermFilters, this.sort.active, this.sort.direction, this.from, this.size, false);
    }

    /**
     * Recherche globale.
     * @param value Valeur de la recherche globale.
     */
    public search(value: string) {
        this.searchTerm = value;
        this.applyFilters();
    }

    /**
     * Initialise la recherche globale
     */
    public resetSearch() {
        this.searchTerm = null;
    }

    /**
     * Génère le filtre de recherche
     */
    private applyGeneralFilters(): void {
        this.searchTermFilters = [];

        if (!!this.searchTerm) {
            if (!!this.model.displayedColumnsDefinitions) {
                this.model.displayedColumnsDefinitions.forEach((item) => {
                    if (item.type == ColumnType.List) {
                        let columnListDefinition = item as ColumnListDefinition<string>;
                        let listValues = '';
                        columnListDefinition.values
                            .filter((a) => a.text.toLowerCase().includes(this.searchTerm.toLowerCase()))
                            .forEach((a) => {
                                listValues += a.value.toString() + '|';
                            });
                        listValues = listValues.substring(0, listValues.length - 1);
                        if (!!listValues) {
                            let filter: FilterInfos = {
                                ColumnName: item.columnName,
                                Value: listValues,
                                Type: item.type,
                                FilterType: this.getFilterType(item.type),
                                SelectionMultiple: true,
                            };
                            this.searchTermFilters.push(filter);
                        }
                    } else {
                        let filter: FilterInfos = {
                            ColumnName: item.columnName,
                            Value: this.searchTerm,
                            Type: item.type,
                            FilterType: this.getFilterType(ColumnType.Text),
                            SelectionMultiple: false,
                        };

                        this.searchTermFilters.push(filter);
                    }
                });
            }
        }
    }

    /**
     * Génére le filtre des colonnes.
     */
    private applyColumnsFilters(): void {
        this.filters = [];

        this.filterHandler.filterredColumnDefinitions.forEach((filteredColumn) => {
            if (!!this.filterHandler.filterValues[filteredColumn.columnName]) {
                let filter: FilterInfos = {
                    ColumnName: filteredColumn.columnName,
                    Value: this.filterHandler.filterValues[filteredColumn.columnName].values.join('|'),
                    Type: filteredColumn.type,
                    FilterType: this.filterHandler.filterValues[filteredColumn.columnName].values?.length > 1 ? FilterType.ListMultiple : this.getFilterType(filteredColumn.type),
                    SelectionMultiple: this.filterHandler.filterValues[filteredColumn.columnName].values?.length > 1,
                };

                this.filters.push(filter);
            }
        });
    }

    private getFilterType(columnType: ColumnType): FilterType {
        let filterType: FilterType = null;
        switch (columnType) {
            case ColumnType.Date:
                filterType = FilterType.Date;
                break;
            case ColumnType.DateAndTime:
                filterType = FilterType.Date;
                break;
            case ColumnType.List:
                filterType = FilterType.ListMultiple;
                break;
            case ColumnType.Numeric:
                filterType = FilterType.Numeric;
                break;
            default:
                filterType = FilterType.Text;
                break;
        }

        return filterType;
    }
}
